
import { Utils } from "../../utils";
import { i18nManager } from "../../i18n";
import { IMember } from "./MemberService";
import { DateHelper, NoseurObject } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IAddress, IAnalyticsData, IEarningSource, IGallery, INotification, IWebsitePage, OmneHostStatus, SanitizedResponse, TransactionFlow, TransactionSourceType, Visibility } from "../BaseService";
import { ITournamentEdition, TournamentService, TournamentTestService } from "./TournamentService";
import { ProgramService, IProgram } from "./ProgramService";
import { NotificationGroup, Notifier } from "../../components/shared/Notifiier";


export enum HostRole {

    OWNER = "OWNER",
    MANAGER = "MANAGER",

}

export enum EntityType {

    BUSINESS = "BUSINESS",
    INDIVIDUAL = "INDIVIDUAL",

}

export enum HostEarningReceiver {

    OWNER = "OWNER",
    SELECTED_HOST_MANAGERS = "SELECTED_HOST_MANAGERS",
    SPLIT_EQUALLY_BETWEEN_HOST_MANAGERS = "SPLIT_EQUALLY_BETWEEN_HOST_MANAGERS",

}

export interface IHost {
    name: string;
    role: HostRole;
    logo_url: string;
    homepage: string;
    public_id: string;
    short_info: string;
    created_at: string;
    external_id: string;
    country_code: string;
    email_address: string;
    status: OmneHostStatus;
    visibility: Visibility;
    entity_type: EntityType;
    extra_data: NoseurObject<any>;
    earning_settings: {
        accepted_terms: boolean;
        receiving_member_ids: string[];
        receiving_group: HostEarningReceiver;
    };
    un_reads?: {
        notifications?: number;
    };
}

export interface IHostManager {
    host: IHost;
    role: HostRole;
    extra_data: any;
    join_date: string;
    external_id: string;
    display_role: string;
    display_name: string;
    profile_details: string;
    member?: Partial<IMember>;
    passport_picture_url: string;
    half_body_picture_url: string;
    social_accounts: NoseurObject<string>;
    settings: {
        country: string;
        state_region: string;
        show_link_to_profile: boolean;
        hide_from_managers_page: boolean;
    };
}

export interface IHostWebsite {
    sponsors: string[];
    visibility: Visibility;
    extra_data: NoseurObject<any>;
    physical_addresses: IAddress[];
    emails_and_phones: {
        email: string;
        phone: string;
    }[];
    social_accounts: NoseurObject<string>;
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    website_pages: {
        HOME: IWebsitePage;
        BLOG: IWebsitePage;
        MERCH: IWebsitePage;
        ABOUT: IWebsitePage;
        EVENTS: IWebsitePage;
        TOURNAMENTS: IWebsitePage;
    };
    mobile_app_pages: {
        BLOG: IWebsitePage;
        MERCH: IWebsitePage;
        ABOUT: IWebsitePage;
        EVENTS: IWebsitePage;
        SEARCH: IWebsitePage;
        SETTINGS: IWebsitePage;
        MESSAGES: IWebsitePage;
        EARNINGS: IWebsitePage;
        DASHBOARD: IWebsitePage;
        TOURNAMENTS: IWebsitePage;
        NOTIFICATIONS: IWebsitePage;
    };
    home_settings: {
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_schedules: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
        show_top_tournaments: boolean;
        show_top_best_tournament_reviews: boolean;
    };
    hero_settings: {
        enable_rivtn_ad: boolean
    },
    website_design_settings: {
        design_id: string;
        scheme_settings: NoseurObject<any>;
    };
    mobile_design_settings: {
        design_id: string;
        scheme_settings: NoseurObject<any>;
    };
    merchandise_settings: {
        storefront_id: string;
        enable_rivtn_ad: boolean;
    },
    tournaments_page_settings: {
        enable_rivtn_ad: boolean;
        show_ended_tournament: boolean;
        show_closed_tournament: boolean;
        show_future_tournament: boolean;
        show_ongoing_tournament: boolean;
        show_cancelled_tournament: boolean;
    },
    events_settings: {
        enable_rivtn_ad: boolean;
    },
    blog_settings: {
        enable_rivtn_ad_among_comments: boolean;
        enable_rivtn_ad_on_articles_page: boolean;
        enable_rivtn_ad_on_categories_page: boolean;
    },
    about_settings: {
        info: string;
        show_people: boolean;
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_physical_address: boolean;
        show_contact_emails_and_phones: boolean;
    };
}

export interface IHostTransaction {
    date: string;
    amount: number;
    external_id: string;
    flow: TransactionFlow;
    source: IEarningSource;
    extra_data?: NoseurObject<any>;
    recipient: IHostManager | HostEarningReceiver;
}

export interface IHostAnalytics {
    programs?: IAnalyticsData;
    tournaments?: IAnalyticsData;
    earnings?: {
        total?: IAnalyticsData;
        tournament?: IAnalyticsData;
        merchandise?: IAnalyticsData;
        advertisement?: IAnalyticsData;
    };
}

export interface IHostManagerEarning {
    amount: number;
    currency: string;
    host_manager: IHostManager;
}

export interface IHostTournamentAnalytics {
    value: number;
    tournament_edition: ITournamentEdition;
}

export interface IHostEvent {
    title: string;
    details: string;
    end_date?: string;
    location?: string;
    start_date?: string;
    external_id: string;
    action_label?: string;
    media_location: string;
    action_location?: string;
}

export interface IHostBlogCategory {
    name: string;
    description: string;
    external_id: string;
    icon_location: string;
}

export interface IHostBlogArticle {
    title: string;
    content: string;
    external_id: string;
    category_id: string;
    extra_data: NoseurObject<any>;
    host_blog_category: IHostBlogCategory;

    featured_image?: string;
}

export class HostTestService {

    static TEST_HOSTS: any[] = [
        {
            created_at: "",
            name: "Karri Secondst",
            public_id: "TH1232342351",
            external_id: "TH1232342351",
            role: HostRole.OWNER,
            visibility: Visibility.PUBLIC,
            country_code: "NGA",
            entity_type: EntityType.BUSINESS,
            logo_url: "https://cdn1.epicgames.com/offer/fn/FNECO_36-10_ForbiddenFruit_EGS_Launcher_KeyArt_Blade_1200x1600_1200x1600-9eb41f23a725f28a00996272bf654e8e",
            extra_data: {
                color_scheme: "235, 216, 52",
                banner_images: [
                    "https://saferschoolsni.co.uk/wp-content/uploads/2021/10/Fortnite1.jpg"
                ],
            },
            un_reads: {
                notifications: 32,
            }
        },
        {
            created_at: "",
            name: "Soccer League",
            public_id: "TH1232342352",
            external_id: "TH1232342352",
            visibility: Visibility.PUBLIC,
            country_code: "NGA",
            role: HostRole.MANAGER,
            entity_type: EntityType.BUSINESS,
            logo_url: "https://img.utdstc.com/icon/7c7/476/7c74763f6b9a76090550b32c7d37d4bfd68b525f93305e3e23d68a5b7be319c2:200",
            extra_data: {
                color_scheme: "13, 217, 37",
                banner_images: [
                    "https://i.ytimg.com/vi/wqTY6qpVCsg/maxresdefault.jpg"
                ]
            },
        },
        {
            created_at: "",
            name: "Soildier Hubs",
            public_id: "TH1232342353",
            external_id: "TH1232342353",
            visibility: Visibility.PUBLIC,
            role: HostRole.OWNER,
            country_code: "NGA",
            entity_type: EntityType.INDIVIDUAL,
            logo_url: "https://sm.ign.com/ign_za/cover/c/call-of-du/call-of-duty-mobile_pc62.jpg",
            extra_data: {
                color_scheme: "227, 53, 9",
                banner_images: [
                    "https://sm.ign.com/ign_za/cover/c/call-of-du/call-of-duty-mobile_pc62.jpg"
                ],
            },
            un_reads: {
                notifications: 4,
            }
        },
    ];

    static TEST_HOST_MANAGERS: any[] = [
        {
            extra_data: {},
            host: {} as any,
            role: HostRole.MANAGER,
            display_role: "Manager",
            profile_details: {} as any,
            join_date: "2025-04-01T19:37:18.000+00:00",
            external_id: "5b5a3030-c59c-406b-9e72-e975cf1d97e6",
            member: { username: "vazrak", profile_banner_url: "https://img.lovepik.com/background/20211021/large/lovepik-colourful-banner-background-image_500374982.jpg" },
            passport_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg",
        },
        {
            extra_data: {},
            external_id: "2",
            host: {} as any,
            role: HostRole.OWNER,
            display_role: "Host",
            profile_details: {} as any,
            join_date: "2025-05-01T19:37:18.000+00:00",
            member: { username: "amsiraceht", profile_banner_url: "https://t4.ftcdn.net/jpg/06/37/22/37/360_F_637223769_KxKjp502bI35Na0dQcQOUp4qCYzdRNIP.jpg", external_id: "5b5a3030-c59c-406b-9e72-e975cf1d97e6" },
            passport_picture_url: "https://images.pexels.com/photos/14653174/pexels-photo-14653174.jpeg",
        },
        {
            extra_data: {},
            external_id: "3",
            host: {} as any,
            display_role: "Host",
            role: HostRole.MANAGER,
            profile_details: {} as any,
            join_date: "2025-06-01T19:37:18.000+00:00",
            member: { username: "lukdreaper", profile_banner_url: "https://t4.ftcdn.net/jpg/06/11/31/69/360_F_611316958_SMSOp3ML81AAxjJsnr7xdMjJQdBTpy8H.jpg" },
            passport_picture_url: "https://wallpapers.com/images/featured/cute-aesthetic-profile-pictures-pjfl391j3q0f7rlz.jpg",
        },
        {
            extra_data: {},
            external_id: "4",
            host: {} as any,
            role: HostRole.MANAGER,
            display_role: "Support",
            profile_details: {} as any,
            join_date: "2025-07-01T19:37:18.000+00:00",
            member: { username: "eccentricity", profile_banner_url: "https://penji.co/wp-content/uploads/2024/09/10_Web_Banner_Examples.jpg.webp" },
            passport_picture_url: "https://www.shutterstock.com/image-photo/man-portrait-smile-outdoor-selfie-260nw-2490416235.jpg",
        },
        {
            extra_data: {},
            external_id: "5",
            host: {} as any,
            role: HostRole.MANAGER,
            display_role: "Support",
            profile_details: {} as any,
            join_date: "2025-08-01T19:37:18.000+00:00",
            member: { username: "YungDon", profile_banner_url: "https://images.squarespace-cdn.com/content/v1/62e3bd1d14b7b32e64a6f856/f595fab2-6020-4e1f-acd6-b11818f35cf4/top+banner+design+tips+.png" },
            passport_picture_url: "https://thumbs.dreamstime.com/b/handsome-attractive-male-adult-person-model-winter-autumn-season-head-shots-face-up-close-expression-portraits-vivid-colors-134123931.jpg",
        },
        {
            extra_data: {},
            external_id: "6",
            host: {} as any,
            role: HostRole.MANAGER,
            display_role: "Finance",
            profile_details: {} as any,
            join_date: "2025-09-01T19:37:18.000+00:00",
            member: { username: "mubamight", profile_banner_url: "https://img.freepik.com/free-vector/christmas-banner-with-stars-design_1048-17557.jpg" },
            passport_picture_url: "https://wallpapers.com/images/hd/cool-profile-picture-87h46gcobjl5e4xu.jpg",
        },
    ];

}

export class HostService extends BaseService {

    protected static instance: HostService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!HostService.instance) HostService.instance = new HostService();
        return HostService.instance;
    }

    queryHosts(params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get("/v1/host", { query: this.normalizeParams(params) }));
    }

    createHost(host: Partial<IHost>): Promise<any> {
        return this.report(this.transport.post("/v1/host", host));
    }

    updateHost(id: string, host: Partial<IHost>): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${id}`, host));
    }

    leaveOrRetireHost(id: string): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/${id}`));
    }

    reactivateHost(id: string): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${id}/activate`, {}));
    }

    getHostByPublicId(id: string): Promise<any> {
        return this.report(this.transport.get(`/v1/host/public/${id}`));
    }

    updateHostEarnings(id: string, payload: any): Promise<any> {
        return this.report(this.transport.put(`/v1/host/${id}/earnings`, payload));
    }

    // invite and manager

    inviteManager(hostId: string, username: string): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.post(`/v1/host/invite/member`, {
            host_id: hostId,
            member_username: username,
            expiry_date: DateHelper.addHours(new Date(), 1),
        }));
    }

    acceptOrRejectHostInvite(id: string, accept: boolean): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.post(`/v1/host/invite/join`, {
            accept_invite: accept,
            host_manager_invite_id: id,
        }));
    }

    removeManager(id: string): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/manager/${id}`));
    }

    transferOwnership(managerId: string): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.put(`/v1/host/manager/${managerId}/ownership`, {}));
    }

    // end invite and manager

    queryHostManagers(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostManager>> {
        params["host.external_id"] = hostId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get("/v1/host/manager", { query: this.normalizeParams(params) }));
    }

    updateHostManager(hostManager: IHostManager): Promise<SanitizedResponse<IHostManager>> {
        return this.report(this.transport.patch(`/v1/host/manager/${hostManager.external_id}`, hostManager));
    }

    // analytics

    getHostAnalytics(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostAnalytics>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code === "KES"
                    ? {
                        programs: {
                            count: 0,
                            value: 0,
                            percentage: 0,
                        },
                        tournaments: {
                            count: 0,
                            value: 0,
                            percentage: 0,
                        },
                        earnings: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                    } as IHostAnalytics
                    : {
                        programs: {
                            count: 10,
                            value: 10,
                            percentage: -5.38,
                        },
                        tournaments: {
                            count: 32,
                            value: 32,
                            percentage: 0.00,
                        },
                        earnings: {
                            total: {
                                count: 30,
                                value: 55600000,
                                percentage: 19.83,
                            },
                            tournament: {
                                count: 10,
                                value: 20454000,
                                percentage: -10.4,
                            },
                            merchandise: {
                                count: 6,
                                value: 20000000,
                                percentage: 98.5,
                            },
                            advertisement: {
                                count: 4,
                                value: 30000000,
                                percentage: 0.0,
                            },
                        },
                    } as IHostAnalytics),
            }
        });
        return this.report(this.transport.get(`/v1/team/${hostId}/analytics`, { query: this.normalizeParams(params) }));
    }

    queryAnalyticsChartData(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    labels: Array(7).fill(0).map((_, i) => i + 1),
                    datasets: [
                        {
                            label: "earnings",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(50000, 500000))
                        },
                        {
                            label: "tournaments",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(20, 30000))
                        },
                        {
                            label: "programs",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(0, 20000))
                        },
                    ]
                }
            }
        }, ({ apiResponse }: SanitizedResponse<any>) => {
            apiResponse.data!.datasets = apiResponse.data!.datasets.map((d: any) => ({
                data: d.data,
                label: (i18nManager.Labels.host.analytics.chart_labels as any)[d.label] ?? d.label,
            }))
        });
        return this.report(this.transport.get(`/v1/team/${hostId}/analytics`, { query: this.normalizeParams(params) }));
    }

    queryTransactions(hostId: string, params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code == "USD"
                    ? {
                        size: 10,
                        content: [],
                        total_pages: 1,
                        number_of_elements: 0,
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        content: (params.size ? HostService.TEST_HOST_TRANSACTIONS.slice(0, params.size as number) : HostService.TEST_HOST_TRANSACTIONS),
                        number_of_elements: params.size ?? HostService.TEST_HOST_TRANSACTIONS.length,
                    })
            }
        });
        return this.report(this.transport.get("/v1/transaction", { query: this.normalizeParams(params) }));
    }

    getHostManagerEarnings(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostManagerEarning>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    size: 10,
                    total_pages: 1,
                    number_of_elements: HostService.TEST_HOST_MANAGER_EARNINGS.length,
                    content: HostService.TEST_HOST_MANAGER_EARNINGS,
                },
            }
        });
        return this.report(this.transport.get(`/v1/team/member/${hostId}/earnings`, { query: this.normalizeParams(params) }));
    }

    getHostTournamentAnalytics(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostTournamentAnalytics>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code == "KES"
                    ? {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: 0,
                        content: [],
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: HostService.TEST_HOST_TOURNAMENT_ANALS.length,
                        content: HostService.TEST_HOST_TOURNAMENT_ANALS,
                    })
            }
        });
        return this.report(this.transport.get(`/v1/team/member/${hostId}/earnings`, { query: this.normalizeParams(params) }));
    }

    // end analytics

    // settings

    getHostWebsite(hostId: string): Promise<SanitizedResponse<IHostWebsite>> {
        return this.report(this.transport.get(`/v1/host/${hostId}/website`));
    }

    updateHostWebsite(hostId: string, hostWebsite: IHostWebsite): Promise<SanitizedResponse<IHostWebsite>> {
        return this.report(this.transport.patch(`/v1/host/${hostId}/website`, hostWebsite));
    }

    getHero(hostId: string): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/host/${hostId}/website/hero`, { query: this.normalizeParams({ size: 100 }) }));
    }

    createHero(hostId: string, hero: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.post(`/v1/host/${hostId}/website/hero`, hero));
    }

    updateHero(hostId: string, hero: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${hostId}/website/hero/${hero.external_id}`, hero));
    }

    deleteHero(hostId: string, hero: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/${hostId}/website/hero/${hero.external_id}`));
    }

    queryHostEvents(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostEvent>> {
        params["host.external_id"] = hostId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get(`/v1/host/${hostId}/website/event`, { query: this.normalizeParams(params) }));
    }

    createHostEvent(hostId: string, event: Partial<IHostEvent>): Promise<any> {
        return this.report(this.transport.post(`/v1/host/${hostId}/website/event`, event));
    }

    updateHostEvent(hostId: string, event: Partial<IHostEvent>): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${hostId}/website/event/${event.external_id}`, event));
    }

    deleteHostEvent(hostId: string, event: Partial<IHostEvent>): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/${hostId}/website/event/${event.external_id}`));
    }

    queryHostBlogCategories(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostBlogCategory>> {
        return this.report(this.transport.get(`/v1/host/${hostId}/blog/category`, { query: this.normalizeParams(params) }));
    }

    createHostBlogCategory(hostId: string, category: Partial<IHostBlogCategory>): Promise<any> {
        return this.report(this.transport.post(`/v1/host/${hostId}/blog/category`, category));
    }

    updateHostBlogCategory(hostId: string, category: Partial<IHostBlogCategory>): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${hostId}/blog/category/${category.external_id}`, category));
    }

    deleteHostBlogCategory(hostId: string, category: Partial<IHostBlogCategory>): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/${hostId}/blog/category/${category.external_id}`));
    }

    queryHostBlogArticles(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<IHostBlogArticle>> {
        params["host.external_id"] = hostId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get(`/v1/host/${hostId}/blog/article`, { query: this.normalizeParams(params) }));
    }

    createHostBlogArticle(hostId: string, article: Partial<IHostBlogArticle>): Promise<any> {
        return this.report(this.transport.post(`/v1/host/${hostId}/blog/article`, article));
    }

    updateHostBlogArticle(hostId: string, article: Partial<IHostBlogArticle>): Promise<any> {
        return this.report(this.transport.patch(`/v1/host/${hostId}/blog/article/${article.external_id}`, article));
    }

    deleteHostBlogArticle(hostId: string, article: Partial<IHostBlogArticle>): Promise<any> {
        return this.report(this.transport.delete(`/v1/host/${hostId}/blog/article/${article.external_id}`));
    }

    // end settings

    // notifications

    queryNotifications(params: NoseurObject<string | number | undefined> = { page: 1, size: 10 }): Promise<SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>> {
        return this.report(this.transport.get("/v1/host/notification", { query: this.normalizeParams(params) }), ({ sanitized }: SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>) => {
            sanitized.grouped_notifications = Notifier.groupNotifications(sanitized.content!);
        });
    }

    markAllNotificationsAsRead(hostId: string): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.put(`/v1/host/notification/${hostId}/read_all`, {}));
    }

    updateNotificationStatus(id: string, status = "READ"): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.patch(`/v1/host/notification/${id}`, { status }));
    }

    // end notifications

    static TEST_HOST_TOURNAMENT_ANALS: IHostTournamentAnalytics[] = [
        {
            value: 70000000,
            tournament_edition: {
                external_id: "1",
                name: "RaceHeads",
                program: ProgramService.TEST_PROGRAMS[14] as IProgram,
                logo_urls: [
                    "https://play-lh.googleusercontent.com/rDAmDCGhyX7FcXYJcR0QrcO04uWgdiQaiWw6oA3T93oK4Xq8qOof0-J_qtw2XFYpKg"
                ],
                banner_urls: [
                    "https://www.teamvvv.com/wp-content/uploads/2016/02/Need-for-Speed-2015-header-art.jpg"
                ],
            } as any,
        },
        {
            value: 60000000,
            tournament_edition: {
                external_id: "2",
                name: "Emperor's Custodes",
                program: ProgramService.TEST_PROGRAMS[15] as IProgram,
                logo_urls: [
                    "https://upload.wikimedia.org/wikipedia/en/4/42/Warhammer_40%2C000_Space_Marine_2_cover_art.jpg"
                ],
                banner_urls: [
                    "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/2183900/ss_66a8bc13bf868d0e2a76e4d797e6408c271065d7.1920x1080.jpg?t=1751643527"
                ],
            } as any,
        },
        {
            value: 5000000,
            tournament_edition: {
                external_id: "3",
                name: "Legends Never Die",
                program: ProgramService.TEST_PROGRAMS[16] as IProgram,
                logo_urls: [
                    "https://upload.wikimedia.org/wikipedia/en/d/db/Apex_legends_cover.jpg"
                ],
                banner_urls: [
                    "https://cdn1.epicgames.com/spt-assets/5dcd88f4e2094a698ebffa43438edc33/apex-legends-1ugyx.jpg?resize=1&w=480&h=270&quality=medium"
                ],
            } as any,
        },
        {
            value: 4000000,
            tournament_edition: {
                external_id: "4",
                name: "Clash Of Kings",
                program: ProgramService.TEST_PROGRAMS[17] as IProgram,
                logo_urls: [
                    "https://sm.ign.com/ign_za/cover/c/clash-roya/clash-royale_88kr.jpg"
                ],
                banner_urls: [
                    "https://i.ytimg.com/vi/uWsQ5IWVilM/maxresdefault.jpg"
                ],
            } as any,
        },
    ];

    static TEST_HOST_MANAGER_EARNINGS: Partial<IHostManagerEarning>[] = [
        {
            currency: "NGN",
            amount: 5909000,
            host_manager: HostTestService.TEST_HOST_MANAGERS[0],
        },
        {
            currency: "NGN",
            amount: 4900020,
            host_manager: HostTestService.TEST_HOST_MANAGERS[1],
        },
        {
            currency: "NGN",
            amount: 3090232,
            host_manager: HostTestService.TEST_HOST_MANAGERS[2],
        },
        {
            currency: "NGN",
            amount: 2090390,
            host_manager: HostTestService.TEST_HOST_MANAGERS[3],
        },
        {
            currency: "NGN",
            amount: 100000,
            host_manager: HostTestService.TEST_HOST_MANAGERS[4],
        },
        {
            currency: "NGN",
            amount: 0,
            host_manager: HostTestService.TEST_HOST_MANAGERS[5],
        },
    ];

    static TEST_HOST_TRANSACTIONS: any[] = [
        {
            amount: 9000023,
            external_id: "1",
            flow: TransactionFlow.IN,
            recipient: HostEarningReceiver.OWNER,
            date: "2025-04-01T19:37:18.000+00:00",
            extra_data: {
                receivers_team_member_ids: [
                    "1"
                ]
            },
            source: {
                id: "1",
                label: "Tournament (Brawl All Out)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 9000023,
            external_id: "2",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[0],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Tournament (Brawl All Out)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 5000000,
            external_id: "3",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostEarningReceiver.SELECTED_HOST_MANAGERS,
            extra_data: {
                receivers_team_member_ids: [
                    "2",
                    "6",
                ]
            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 2500000,
            external_id: "4",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 2500000,
            external_id: "5",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[5],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Merch Sales (Team Logo + Shirt)",
                type: TransactionSourceType.MERCHANDISE,
            },
        },
        {
            amount: 70123,
            external_id: "6",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostEarningReceiver.SELECTED_HOST_MANAGERS,
            extra_data: {
                receivers_team_member_ids: [
                    "2",
                ]
            },
            source: {
                id: "1",
                label: "Tournament (Chicken Dinnerers)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 70123,
            external_id: "7",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Tournament (Chicken Dinnerers)",
                type: TransactionSourceType.TOURNAMENT,
            },
        },
        {
            amount: 10000000,
            external_id: "8",
            flow: TransactionFlow.IN,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostEarningReceiver.SPLIT_EQUALLY_BETWEEN_HOST_MANAGERS,
            extra_data: {
                receivers_team_member_ids: [
                    "1",
                    "2",
                    "3",
                    "4",
                    "5",
                    "6",
                ]
            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "9",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[0],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "10",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[1],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "11",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[2],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "12",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[3],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "13",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[4],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
        {
            amount: 1666666,
            external_id: "14",
            flow: TransactionFlow.OUT,
            date: "2025-04-01T19:37:18.000+00:00",
            recipient: HostTestService.TEST_HOST_MANAGERS[5],
            extra_data: {

            },
            source: {
                id: "1",
                label: "Rivtn Advertisement",
                type: TransactionSourceType.ADVERTISEMENT,
            },
        },
    ];

    static TEST_HOST_HEROS: Partial<IGallery>[] = [
        {
            external_id: "1",
            caption: "wassup",
            action_label: "Dezz Nuts",
            action_location: "https://rivtn.com",
            media_location: "https://lh3.googleusercontent.com/4PYv0EMR96DGOkcPRK7ZTU6IoAYMwYM7M_aPDieJNlZ0HFY-lUKMHwB-DbOUfwBo7EHJuBXFpsTbioA=w544-h544-l90-rj",
        },
        {
            external_id: "2",
            action_label: "Prof Seyi",
            action_location: "https://rivtn.com",
            media_location: "https://lh3.googleusercontent.com/ygYBwJiprrDb8HHVlAdxqBrzoJyl0ngetHFhnzUM-4FrNa5MS9p67GJiUSVmaAHrclWOPBOeRjCy3ki4=w544-h544-l90-rj",
        },
    ];

    static TEST_HOST_BLOG_CATEGORIES: IHostBlogCategory[] = [
        {
            external_id: "1",
            name: "Tournaments",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://img.freepik.com/premium-vector/tournament-sports-league-logo-emblem_1366-202.jpg",
        },
        {
            external_id: "2",
            name: "Team Management",
            description: "Terms Of Service, Online Safety, Personal Data. erms Of Service, Online Safety, Personal Data. Terms Of Service, Online Safety, Personal Data. erms Of Service, Online Safety, Personal Data. Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://cdn-icons-png.flaticon.com/512/10007/10007857.png",
        },
        {
            external_id: "3",
            name: "Rules And Regulations",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-illustration/rules-regulations-blue-stamp-text-260nw-320582720.jpg",
        },
        {
            external_id: "4",
            name: "Prize Pool Sharing ",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.pngfind.com/pngs/m/338-3387437_nfl-sunday-b-o-o-m-11-250.png",
        },
        {
            external_id: "5",
            name: "Precautions on creation",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-vector/precaution-icon-linear-vector-illustration-260nw-2218642131.jpg",
        },
        {
            external_id: "6",
            name: "Tournaments",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://img.freepik.com/premium-vector/tournament-sports-league-logo-emblem_1366-202.jpg",
        },
        {
            external_id: "7",
            name: "Team Management",
            description: "Terms Of Service, Online Safety, Personal Data. erms Of Service, Online Safety, Personal Data. Terms Of Service, Online Safety, Personal Data. erms Of Service, Online Safety, Personal Data. Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://cdn-icons-png.flaticon.com/512/10007/10007857.png",
        },
        {
            external_id: "8",
            name: "Rules And Regulations",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-illustration/rules-regulations-blue-stamp-text-260nw-320582720.jpg",
        },
        {
            external_id: "9",
            name: "Prize Pool Sharing ",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.pngfind.com/pngs/m/338-3387437_nfl-sunday-b-o-o-m-11-250.png",
        },
        {
            external_id: "10",
            name: "Precautions on creation",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-vector/precaution-icon-linear-vector-illustration-260nw-2218642131.jpg",
        },
        {
            external_id: "11",
            name: "Rules And Regulations",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-illustration/rules-regulations-blue-stamp-text-260nw-320582720.jpg",
        },
        {
            external_id: "12",
            name: "Prize Pool Sharing ",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.pngfind.com/pngs/m/338-3387437_nfl-sunday-b-o-o-m-11-250.png",
        },
        {
            external_id: "13",
            name: "Precautions on creation",
            description: "Terms Of Service, Online Safety, Personal Data.",
            icon_location: "https://www.shutterstock.com/image-vector/precaution-icon-linear-vector-illustration-260nw-2218642131.jpg",
        },
    ];

    static TEST_HOST_EVENTS: IHostEvent[] = [
        {
            external_id: "1",
            action_label: "Register",
            action_location: "https://location.com",
            title: "The new hosting user interface",
            end_date: "2025-12-01T19:37:18.000+00:00",
            start_date: "2025-04-01T19:37:18.000+00:00",
            location: "The Expo Center, Lagos, Nigeria",
            media_location: "https://marketplace.canva.com/EAGIZFpqX6o/1/0/1131w/canva-black-and-purple-modern-night-party-flyer-wIFfa5B55LY.jpg",
            details: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "2",
            action_label: "Join",
            title: "The new hosting user interface",
            end_date: "2025-06-01T19:37:18.000+00:00",
            start_date: "2025-04-01T19:37:18.000+00:00",
            location: "The Expo Center, Lagos, Nigeria",
            media_location: "https://d1csarkz8obe9u.cloudfront.net/posterpreviews/party-flyers-design-template-136c9dbbeb33e2410bcb4770147addcb_screen.jpg?ts=1637042032",
            details: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "3",
            title: "The new hosting user interface",
            location: "The Expo Center, Lagos, Nigeria",
            start_date: "2025-04-01T19:37:18.000+00:00",
            media_location: "https://resourceboy.com/wp-content/uploads/2023/03/tropical-retro-sunset-party-event-flyer-template.jpg",
            details: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "4",
            title: "The new hosting user interface",
            location: "The Expo Center, Lagos, Nigeria",
            media_location: "https://mir-s3-cdn-cf.behance.net/project_modules/fs/b0d0f2123205579.60e987d4b89f6.jpg",
            details: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "5",
            title: "The new hosting user interface",
            location: "The Expo Center, Lagos, Nigeria",
            start_date: "2025-12-01T19:37:18.000+00:00",
            media_location: "https://d1csarkz8obe9u.cloudfront.net/posterpreviews/birthday-party-flyer-%281%29-design-template-936b5cb2bb81d74cba03045530eb9884_screen.jpg?ts=1658821591",
            details: "The enabling is done async  messaging channel for all NIgeria related email sending opetrations across the galaxy and through put the universe to the multiver to encompass the infinite omniverse.",
        },
        {
            external_id: "8",
            title: "Setting up your team profile",
            media_location: "https://i.pinimg.com/564x/ce/21/76/ce21765b01256e5c454799156979c8ab.jpg",
            details: `
    [](https://github.com/exoticlibraries/libcester/raw/main/docs/libcester.png)
    
    # libcester
    
    A robust header only unit testing framework for C programming language. Support function mocking, memory leak detection, crash report. 
    
    cester is a header only automated testing framework for the C programming language, it requires no dependency and can be downloaded and used in a project immediately. Works on various platorms including embedded systems and compatible with various compilers. Allows shared instance \`TestInstance\` object in which each test cases can use to share data and access the command line arguments. 
    
    \`\`\`c\n#include <exotic/cester.h>
    
    CESTER_TEST(test_one, inst,\n    cester_assert_equal(NULL, ((void*)0));    
    
    \`\`\`
    
    The test results can be outputed as various format JunitXML, Test Anything Protocol, Test Anything Protocol Version 13 and text. Visit [https://exoticlibraries.github.io/libcester/docs/](https://exoticlibraries.github.io/libcester/docs/) for documentation and tutorials. 
    ___
    
    cester is a header only automated testing framework for the C programming language, it requires no dependency and can be downloaded and used in a project immediately. Works on various platorm            
                `,
        },
    ];

}
